/**
 * Extracted report filter configuration builder.
 *
 * Maps report filter definitions (from the API) to FilterConfig objects
 * that can be rendered by the Filters component.
 *
 * Used by:
 * - ReportFilters.tsx (Reports page)
 * - CustomAction.tsx (Automation report filter editing)
 */

import { FC } from 'react';
import { BillingStatus } from 'src/models/client/_types';
import { Report, ReportFilter } from 'src/models/report/_types';

import { AccountFilter } from './AccountFilter';
import { AffiliateCodeFilter } from './AffiliateCodeFilter';
import { AnniversaryAndBirthdayFilter } from './AnniversaryAndBirthdayFilter';
import { AppPlatformFilter } from './AppPlatformFilter';
import { AssessmentFilter } from './AssessmentFilter';
import { AutocompleteFilter } from './AutocompleteFilter';
import { CalendarFilter } from './CalendarFilter';
import { ChargeStatusFilter } from './ChargeStatusFilter';
import { CheckboxFilter } from './CheckboxFilter';
import { ChurnConditionsFilter } from './ChurnConditionsFilter';
import { ChurnEventsFilter } from './ChurnEventsFilter';
import { ClientCustomStatusFilter } from './ClientCustomStatusFilter';
import { ClientFilter } from './ClientFilter';
import { CombinedFilter } from './CombinedFilter';
import { ConvertedFilter } from './ConvertedFilter';
import { CouponFilter } from './CouponFilter';
import { CustomProfileFieldFilter } from './CustomProfileFieldFilter';
import { DetailedViewFilter } from './DetailedViewFilter';
import { ExerciseFilter } from './ExerciseFilter';
import { GroupFilter } from './GroupFilter';
import { GuestFilter } from './GuestFilter';
import { ItemFilter } from './ItemFilter';
import { ItemTypeFilter } from './ItemTypeFilter';
import { LeadAssessmentFilter } from './LeadAssessmentFilter';
import { LifecycleStageFilter } from './LifecycleStageFilter';
import { LocationFilter } from './LocationFilter';
import { MailingFilter } from './MailingFilter';
import { MeasurementFilter } from './MeasurementFilter';
import { MiaFilter } from './MiaFilter';
import { PackageCategoryFilter } from './PackageCategoryFilter';
import { PackageFilter } from './PackageFilter';
import { PackagePaymentTypesFilter } from './PackagePaymentTypesFilter';
import { PaymentTypeFilter } from './PaymentTypeFilter';
import { ProductFilter } from './ProductFilter';
import { ProductTagFilter } from './ProductTagFilter';
import { PurchaseTypeFilter } from './PurchaseTypeFilter';
import { ReportDateFilter } from './ReportDateFilters';
import { ResourcesFilter } from './ResourcesFilter';
import { SelectFilter } from './SelectFilter';
import { ServiceCategoryFilter } from './ServiceCategoryFilter';
import { ServiceFilter } from './ServiceFilter';
import { SiteFilter } from './SiteFilter';
import { StartedOrActiveSubscriptionsFilter } from './StartedOrActiveSubscriptionsFilter';
import { SubItemTypeFilter } from './SubItemTypeFilter';
import { SubscriptionTypeFilter } from './SubscriptionTypeFilter';
import { TagsFilter } from './TagsFilter';
import { TimeIntervalFilter } from './TimeIntervalFilter';
import { TrainerFilter } from './TrainerFilter';
import { VisitStatusFilter } from './VisitStatusFilter';
import { WebhookEventFilter } from './WebhookEventFilter';
import { WorkoutPlanFilter } from './WorkoutPlanFilter';
import { WorkoutPlanTagFilter } from './WorkoutPlanTagFilter';

export interface ReportFilterConfig {
  field: string;
  label: string;
  Component: FC<any>;
  description?: string;
  props?: Record<string, any>;
  required?: boolean;
  showBlank?: boolean;
  disabled?: boolean;
}

export interface ReportFilterBuildOptions {
  /** The full Report object (used by ReportDateFilter) */
  report?: Partial<Report>;
  /** Override filter definitions for the date filter component */
  reportConfigFilters?: ReportFilter[];
  /** Whether to disable the account filter (e.g., incompatible payment type selected) */
  shouldDisableAccountFilter?: boolean;
  /** Text to show when account filter is disabled */
  accountFilterDisabledText?: string;
  /** Whether to restrict the sites filter to the user's locations */
  shouldRestrictToMyLocations?: boolean;
}

/**
 * Maps an array of ReportFilter definitions to renderable FilterConfig objects.
 *
 * This is the single source of truth for mapping report filter IDs (from the API)
 * to the corresponding React filter components.
 */
export function buildReportFilterConfigs(
  reportFilters: ReportFilter[],
  options: ReportFilterBuildOptions = {}
): ReportFilterConfig[] {
  const {
    report,
    reportConfigFilters,
    shouldDisableAccountFilter = false,
    accountFilterDisabledText,
    shouldRestrictToMyLocations = false,
  } = options;

  return reportFilters
    .map((filter): ReportFilterConfig | undefined => {
      switch (filter.id) {
        case 'futureDate':
        case 'single_date':
        case 'date':
          return {
            field: 'date',
            label: '',
            Component: (props: any) => (
              <ReportDateFilter
                {...props}
                report={{
                  ...report,
                  filters: reportConfigFilters ?? report?.filters ?? [],
                }}
              />
            ),
          };
        case 'secondary_date':
          return {
            field: 'secondaryDate',
            label: filter.label,
            Component: (props: any) => (
              <ReportDateFilter
                {...props}
                report={{ filters: [{ id: 'date', label: filter.label }] }}
              />
            ),
          };
        case 'account':
          return {
            field: 'accountId',
            label: filter.label,
            Component: AccountFilter,
            description: shouldDisableAccountFilter
              ? accountFilterDisabledText
              : filter.description,
            disabled: shouldDisableAccountFilter,
            required: filter.required,
          };
        case 'serviceTag':
          return {
            field: 'serviceTag',
            label: filter.label,
            Component: ServiceCategoryFilter,
            required: filter.required,
          };
        case 'multi-service':
        case 'service':
          return {
            field: 'serviceId',
            label: filter.label,
            Component: (props: any) => (
              <ServiceFilter
                {...props}
                multiple={filter.id === 'multi-service'}
              />
            ),
            description: filter.description,
            required: filter.required,
          };
        case 'multi-trainer':
        case 'trainer':
          return {
            field: 'trainerId',
            label: filter.label,
            description: filter.description,
            Component: (props: any) => (
              <TrainerFilter
                {...props}
                multiple={filter.id === 'multi-trainer'}
              />
            ),
            required: filter.required,
          };
        case 'multi-location':
        case 'location':
          return {
            field: 'locationId',
            label: filter.label,
            description: filter.description,
            Component: (props: any) => (
              <LocationFilter
                {...props}
                multiple={filter.id === 'multi-location'}
              />
            ),
            required: filter.required,
          };
        case 'client_location':
          return {
            field: 'clientLocationId',
            label: filter.label,
            description: filter.description,
            Component: (props: any) => (
              <LocationFilter
                {...props}
                multiple={true}
                includeNoLocation={true}
              />
            ),
            required: filter.required,
          };
        case 'session_location':
          return {
            field: 'sessionLocationId',
            label: filter.label,
            description: filter.description,
            Component: (props: any) => (
              <LocationFilter
                {...props}
                multiple={filter.id === 'multi-location'}
              />
            ),
            required: filter.required,
          };
        case 'group':
          return {
            field: 'groupId',
            label: filter.label,
            Component: GroupFilter,
            required: filter.required,
          };
        case 'packageTag':
          return {
            field: 'packageTag',
            label: filter.label,
            Component: (props: any) => (
              <PackageCategoryFilter {...props} multiple={true} />
            ),
            required: filter.required,
          };
        case 'custom_profile_field':
          return {
            field: 'customProfileField',
            label: filter.label,
            Component: CustomProfileFieldFilter,
            required: filter.required,
          };
        case 'custom_status':
          return {
            field: 'customStatus',
            label: filter.label,
            Component: ClientCustomStatusFilter,
            required: filter.required,
          };
        case 'client_filter_type':
          return {
            field: 'clientFilterType',
            label: filter.label,
            Component: ClientCustomStatusFilter,
            required: filter.required,
          };
        case 'workout_plan':
          return {
            field: 'workoutPlanId',
            label: filter.label,
            Component: WorkoutPlanFilter,
            required: filter.required,
          };
        case 'calendar':
          return {
            field: 'calendarId',
            label: filter.label,
            Component: CalendarFilter,
            required: filter.required,
          };
        case 'package':
          return {
            field: 'packageId',
            label: filter.label,
            description: filter.description,
            Component: PackageFilter,
            required: filter.required,
          };
        case 'include_packages':
          return {
            field: 'includePackages',
            label: filter.label,
            Component: (props: any) => (
              <PackageFilter {...props} multiple={true} />
            ),
            required: filter.required,
          };
        case 'owned_packages':
          return {
            field: 'ownsPackage',
            label: filter.label,
            Component: (props: any) => (
              <PackageFilter {...props} multiple={true} />
            ),
            required: filter.required,
          };
        case 'resources':
          return {
            field: 'resources',
            label: filter.label,
            Component: ResourcesFilter,
            required: filter.required,
          };
        case 'client_status':
          return {
            field: 'client_status',
            label: filter.label,
            Component: SelectFilter,
            props: {
              options: Object.values(BillingStatus).map((v) => ({
                value: v,
                label: v,
              })),
            },
            required: filter.required,
          };
        case 'client_tags':
          return {
            field: 'clientTags',
            label: filter.label,
            Component: TagsFilter,
            required: filter.required,
          };
        case 'client_tag':
          return {
            field: 'miaClientTags',
            label: filter.label,
            Component: TagsFilter,
            required: filter.required,
          };
        case 'mia':
          return {
            field: 'mia',
            label: filter.label,
            Component: MiaFilter,
            required: filter.required,
          };
        case 'product':
          return {
            field: 'productId',
            label: filter.label,
            Component: ProductFilter,
            required: filter.required,
          };
        case 'product_tag':
          return {
            field: 'productTag',
            label: filter.label,
            Component: ProductTagFilter,
            required: filter.required,
          };
        case 'workout_plan_tag':
          return {
            field: 'workoutPlanTag',
            label: filter.label,
            Component: WorkoutPlanTagFilter,
            required: filter.required,
          };
        case 'item_type':
          return {
            field: 'itemType',
            label: filter.label,
            Component: ItemTypeFilter,
            required: filter.required,
          };
        case 'sub_item_type':
          return {
            field: 'itemType',
            label: filter.label,
            Component: SubItemTypeFilter,
            required: filter.required,
          };
        case 'item_id':
          return {
            field: 'itemId',
            label: filter.label,
            Component: ItemFilter,
            required: filter.required,
          };
        case 'sub_item_id':
          return {
            field: 'itemId',
            label: filter.label,
            Component: (props: any) => (
              <ItemFilter {...props} type="subscription" />
            ),
            required: filter.required,
          };
        case 'measurement':
          return {
            field: 'measurement',
            label: filter.label,
            Component: MeasurementFilter,
            required: filter.required,
          };
        case 'exercise':
          return {
            field: 'exerciseId',
            label: filter.label,
            Component: ExerciseFilter,
            required: filter.required,
          };
        case 'client':
          return {
            field: 'clientId',
            label: filter.label,
            Component: ClientFilter,
            required: filter.required,
          };
        case 'time_interval':
          return {
            field: 'timeInterval',
            label: filter.label,
            Component: TimeIntervalFilter,
            required: filter.required,
          };
        case 'leadAssessment':
          return {
            field: 'leadAssessment',
            label: filter.label,
            Component: LeadAssessmentFilter,
            description: filter.description,
            required: filter.required,
            showBlank: true,
          };
        case 'combined':
          return {
            field: 'combined',
            label: filter.label,
            Component: CombinedFilter,
            required: filter.required,
          };
        case 'coupon':
          return {
            field: 'couponCode',
            label: filter.label,
            Component: CouponFilter,
            required: filter.required,
          };
        case 'detailed_view':
          return {
            field: 'detailedView',
            label: filter.label,
            Component: DetailedViewFilter,
            required: filter.required,
          };
        case 'mailing':
          return {
            field: 'mailing',
            label: filter.label,
            Component: MailingFilter,
            required: filter.required,
          };
        case 'payment_type':
          return {
            field: 'paymentType',
            label: filter.label,
            Component: PaymentTypeFilter,
            required: filter.required,
          };
        case 'purchase_type':
          return {
            field: 'purchaseType',
            label: filter.label,
            Component: PurchaseTypeFilter,
            required: filter.required,
          };
        case 'from_lifecycle_stage':
          return {
            field: 'fromLifecycleStage',
            label: filter.label,
            Component: (props: any) => (
              <LifecycleStageFilter {...props} multiple />
            ),
            required: filter.required,
          };
        case 'to_lifecycle_stage':
          return {
            field: 'toLifecycleStage',
            label: filter.label,
            Component: (props: any) => (
              <LifecycleStageFilter {...props} multiple />
            ),
            required: filter.required,
          };
        case 'lifecycle_stage':
          return {
            field: 'lifecycleStage',
            label: filter.label,
            description: filter.description,
            Component: (props: any) => (
              <LifecycleStageFilter {...props} multiple />
            ),
            required: filter.required,
          };
        case 'include_failed':
          return {
            field: 'includeFailed',
            label: filter.label,
            Component: CheckboxFilter,
            required: filter.required,
          };
        case 'inactive':
          return {
            field: 'inactive',
            label: filter.label,
            Component: CheckboxFilter,
            required: filter.required,
          };
        case 'exclude_frozen':
          return {
            field: 'excludeFrozen',
            label: filter.label,
            Component: CheckboxFilter,
            required: filter.required,
          };
        case 'exclude_noshow':
          return {
            field: 'excludeNoshow',
            label: filter.label,
            Component: CheckboxFilter,
            required: filter.required,
          };
        case 'webhook_event':
          return {
            field: 'webhookEvent',
            label: filter.label,
            Component: WebhookEventFilter,
            required: filter.required,
          };
        case 'assessment':
          return {
            field: 'assessmentId',
            label: filter.label,
            Component: AssessmentFilter,
            required: filter.required,
          };
        case 'assessmentQuestions':
          return {
            field: 'assessmentQuestions',
            label: filter.label,
            Component: CheckboxFilter,
            required: filter.required,
          };
        case 'packagePaymentType':
          return {
            field: 'packagePaymentTypes',
            label: filter.label,
            Component: PackagePaymentTypesFilter,
            required: filter.required,
          };
        case 'affiliate_code':
          return {
            field: 'affiliateCode',
            label: filter.label,
            Component: AffiliateCodeFilter,
            required: filter.required,
          };
        case 'subscription_type':
          return {
            field: 'subscriptionType',
            label: filter.label,
            Component: SubscriptionTypeFilter,
            required: filter.required,
          };
        case 'charge_status':
          return {
            field: 'chargeStatus',
            label: filter.label,
            Component: ChargeStatusFilter,
            required: filter.required,
          };
        case 'visit_status':
          return {
            field: 'visitStatus',
            label: filter.label,
            Component: VisitStatusFilter,
            required: filter.required,
          };
        case 'booked_by_type':
          return {
            field: 'bookedByType',
            label: filter.label,
            Component: SelectFilter,
            props: {
              options: [
                { value: 'staff', label: 'Booked by Staff' },
                { value: 'client', label: 'Booked by Client' },
              ],
            },
            required: filter.required,
          };
        case 'startedOrActive':
          return {
            field: 'subscriptionStatus',
            label: filter.label,
            Component: StartedOrActiveSubscriptionsFilter,
            required: filter.required,
          };
        case 'disputed':
          return {
            field: 'disputed',
            label: filter.label,
            Component: CheckboxFilter,
            required: filter.required,
          };
        case 'guest':
          return {
            field: 'guest',
            label: filter.label,
            Component: GuestFilter,
            description: filter.description,
            required: filter.required,
          };
        case 'churn_events':
          return {
            field: 'churnEvents',
            label: filter.label,
            Component: ChurnEventsFilter,
            required: filter.required,
          };
        case 'churn_conditions':
          return {
            field: 'churnConditions',
            label: filter.label,
            Component: ChurnConditionsFilter,
            required: filter.required,
          };
        case 'only_active':
          return {
            field: 'onlyActive',
            label: filter.label,
            Component: CheckboxFilter,
            required: filter.required,
          };
        case 'remove_paused':
          return {
            field: 'removePaused',
            label: filter.label,
            Component: CheckboxFilter,
            required: filter.required,
          };
        case 'show_zero_visits':
          return {
            field: 'showZeroVisits',
            label: filter.label,
            Component: CheckboxFilter,
            required: filter.required,
          };
        case 'only_unused_visits':
          return {
            field: 'onlyUnusedVisits',
            description: filter.description,
            label: filter.label,
            Component: CheckboxFilter,
            required: filter.required,
          };
        case 'no_date':
          return {
            field: 'noDate',
            label: filter.label,
            description: filter.description,
            Component: CheckboxFilter,
            required: filter.required,
          };
        case 'has_upcoming_visit':
          return {
            field: 'hasUpcomingVisit',
            label: filter.label,
            Component: CheckboxFilter,
            required: filter.required,
          };
        case 'converted':
          return {
            field: 'converted',
            label: filter.label || 'Converted',
            Component: ConvertedFilter,
            required: filter.required,
          };
        case 'converted_item':
          return {
            field: 'convertedItem',
            label: filter.label || 'Converted Item',
            Component: ConvertedFilter,
            required: filter.required,
          };
        case 'app_platform':
          return {
            field: 'appPlatform',
            label: filter.label,
            Component: AppPlatformFilter,
            required: filter.required,
          };
        case 'anniversary':
          return {
            field: 'anniversary',
            label: filter.label,
            description: filter.description,
            Component: AnniversaryAndBirthdayFilter,
            required: filter.required,
          };
        case 'birthday':
          return {
            field: 'birthday',
            label: filter.label,
            description: filter.description,
            Component: AnniversaryAndBirthdayFilter,
            required: filter.required,
          };
        case 'sites':
          return {
            field: 'sites',
            label: filter.label,
            Component: (props: any) => (
              <SiteFilter
                {...props}
                showMyLocationsOnly={shouldRestrictToMyLocations}
              />
            ),
            required: filter.required,
          };
        case 'smsStatus':
          return {
            field: 'smsStatus',
            label: filter.label,
            description: filter.description,
            Component: SelectFilter,
            props: {
              options: [
                { value: 'opted_in', label: 'Opted In' },
                { value: 'opted_out', label: 'Opted Out' },
              ],
            },
            required: filter.required,
          };
          case 'relationship':
            return {
              field: 'relationship',
              label: filter.label,
              description: filter.description,
              Component: AutocompleteFilter,
              props: {
                options: [
                  { id: 'Unassigned', name: 'Unassigned' },
                  { id: 'Parent', name: 'Parent' },
                  { id: 'Child', name: 'Child' },
                  { id: 'Partner', name: 'Partner' },
                  { id: 'Sibling', name: 'Sibling' },
                  { id: 'Friend', name: 'Friend' },
                ],
                multiple: true,
              },
              required: filter.required,
            };
        default:
          return undefined;
      }
    })
    .filter((f): f is ReportFilterConfig => !!f);
}
